import { useTranslation } from 'react-i18next'
import { FiCalendar } from 'react-icons/fi'
import { ListItemCard, ListItemRow } from 'src/components/layout/item-collection'
import type { ViewMode } from 'src/hooks/use-view-mode'
import { ExpenseReport } from 'src/services/expense-reports'
import { cn } from 'src/utils/style'
import { getTimeAgo } from 'src/utils/utils'

interface ReportCollectionItemProps {
  report: ExpenseReport
  viewMode: ViewMode
  onClick: () => void
}

export const ReportCollectionItem = ({ report, viewMode, onClick }: ReportCollectionItemProps) => {
  const { t } = useTranslation()
  const generatedAgo = report.dateReportGenerated ? getTimeAgo(report.dateReportGenerated) : ''

  const statusBadge = report.status ? (
    <span
      className={cn([
        'shrink-0 rounded px-2 py-1 text-xs font-semibold',
        report.status === 'Approved'
          ? 'bg-green-200 text-green-800'
          : 'bg-primary-200 text-primary-800',
      ])}
    >
      {report.status}
    </span>
  ) : null

  if (viewMode === 'list') {
    return (
      <ListItemRow onClick={onClick} data-testid={`report-row-${report.id}`}>
        <div className="min-w-0">
          <h3 className="text-left font-semibold">
            {report.name}
            {report.organizationRef ? ` - ${report.organizationRef.name}` : ''}
          </h3>
          <p className="flex items-center gap-2 text-sm text-text-placeholder">
            <FiCalendar className="inline h-4 w-4" /> {generatedAgo}
            {statusBadge && <span className="ml-2">{statusBadge}</span>}
          </p>
        </div>
      </ListItemRow>
    )
  }

  return (
    <ListItemCard onClick={onClick} data-testid={`report-card-${report.id}`}>
      {/* Header */}
      <div className="flex min-w-0 items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-text-primary" title={report.name}>
            {report.name}
          </h3>
          {report.organizationRef && (
            <p
              className="truncate text-sm text-text-placeholder"
              title={report.organizationRef.name}
            >
              {report.organizationRef.name}
            </p>
          )}
        </div>
        {statusBadge}
      </div>

      {/* Key fields */}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('reportsPage.template')}</dt>
          <dd
            className="truncate font-medium text-text-primary"
            title={report.defaultTemplateRef?.name || undefined}
          >
            {report.defaultTemplateRef?.name || '-'}
          </dd>
        </div>
        <div className="min-w-0">
          <dt className="text-xs text-text-secondary">{t('reportsPage.submitted')}</dt>
          <dd className="truncate font-medium text-text-primary">
            {report.isSubmitted ? t('common.yes') : t('common.no')}
          </dd>
        </div>
      </dl>

      {/* Last generated */}
      <div className="mt-auto flex min-w-0 items-center justify-between gap-3 border-t border-border pt-3 text-sm">
        <span className="shrink-0 text-text-secondary">{t('reportsPage.generated')}</span>
        <span className="flex min-w-0 items-center gap-1 font-medium text-text-primary">
          <FiCalendar className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span className="truncate">{generatedAgo || '-'}</span>
        </span>
      </div>
    </ListItemCard>
  )
}
